import { useEffect, useRef, useState } from 'react';
import { logger } from '../../utils/logger';

const POLL_INTERVAL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 4_000;     // endpoint internally caps at 3 s
const WARN_THRESHOLD_MS = 500;        // amber: noticeable
const CRIT_THRESHOLD_MS = 1_500;      // crimson: degraded for sure

/**
 * Topbar pill that surfaces backend Mongo health (`/api/health/db`).
 *
 * Hidden when the round-trip is healthy (< 500 ms) — the operator only
 * sees it when something deserves attention. Amber on slow latency,
 * crimson + breathing pulse on degraded / unreachable.
 *
 * Bypasses the shared axios client because:
 *   1. the endpoint is unauthenticated, so we don't need cookies
 *   2. axios `api` retries 5xx automatically — wasted RTTs while DB is bad
 *   3. lets us scope a tight per-request timeout
 */
export function DbLatencyPill() {
  const [state, setState] = useState({ status: 'ok', latencyMs: 0, ts: null, error: null });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/health/db`;

    const poll = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      const startedAt = performance.now();
      try {
        const res = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        const body = await res.json().catch(() => ({}));
        if (!mountedRef.current) return;
        if (res.ok && body.status === 'ok') {
          setState({
            status: 'ok',
            latencyMs: body.latencyMs ?? Math.round(performance.now() - startedAt),
            ts: Date.now(),
            error: null,
          });
        } else {
          setState({
            status: 'degraded',
            latencyMs: null,
            ts: Date.now(),
            error: body.error || `http_${res.status}`,
          });
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setState({
          status: 'degraded',
          latencyMs: null,
          ts: Date.now(),
          error: err.name === 'AbortError' ? 'probe_timeout' : 'unreachable',
        });
        logger.warn?.('DB latency probe failed:', err);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, []);

  const isCrit = state.status !== 'ok' || (state.latencyMs ?? 0) >= CRIT_THRESHOLD_MS;
  const isWarn = state.status === 'ok' && state.latencyMs >= WARN_THRESHOLD_MS && state.latencyMs < CRIT_THRESHOLD_MS;
  if (!isCrit && !isWarn) return null;

  const label = state.status === 'ok' ? `DB ${state.latencyMs}ms` : 'DB offline';
  const probedAt = state.ts ? new Date(state.ts).toLocaleTimeString() : '—';
  const title = state.status === 'ok'
    ? `MongoDB ping latency: ${state.latencyMs} ms — last probed at ${probedAt}`
    : `MongoDB unreachable (${state.error}) — last probed at ${probedAt}`;

  return (
    <div
      data-testid="db-latency-pill"
      data-state={isCrit ? 'crit' : 'warn'}
      role="status"
      aria-live="polite"
      title={title}
      className="pd-status-pill"
    >
      <span className="pd-dot" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
